import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { FLOW_START_NODE, getFlowNode } from './Flow.ts';
import {
  AppState,
  FlowAction,
  FlowContext,
  FlowOption,
  FlowState,
  NodeId,
  RearMountedChildBikeSeatsData,
  RearMountedChildBikeSeatsResponse,
} from './Types/Types';
import { loadCbsAnswers, removeCbsAnswers, saveCbsAnswers } from './Helpers/CookieHelper.ts';
import { ResetFitStatusForAllProductsInListing } from './Helpers/StatusHelper.ts';
import { IsProductPage } from '../FitAssistantShared/IsProductPage.js';

const AppStateContext = createContext({} as AppState);

const initialFlowState: FlowState = {
  currentNodeId: FLOW_START_NODE,
  history: [],
  answers: {},
  answerLabels: {},
};

const clearFutureAnswers = (state: FlowState, keysToClear: NodeId[]): FlowState => {
  if (!keysToClear.length) {
    return state;
  }
  const answers = { ...state.answers };
  const answerLabels = { ...state.answerLabels };
  keysToClear.forEach((key) => {
    delete answers[key];
    delete answerLabels[key];
  });
  return { ...state, answers, answerLabels };
};

const reducer = (state: FlowState, action: FlowAction): FlowState => {
  if (action.type === 'reset') {
    return { ...initialFlowState };
  }

  if (action.type === 'restore') {
    return action.flowState;
  }

  if (action.type === 'back') {
    if (state.history.length === 0) {
      return state;
    }
    const currentNode = getFlowNode(state.currentNodeId);
    const previousNode = state.history[state.history.length - 1];
    const previousFlowNode = getFlowNode(previousNode);
    const nextState = {
      ...state,
      currentNodeId: previousNode,
      history: state.history.slice(0, -1),
    };

    const keysToClear: NodeId[] = [];
    if (currentNode.answerKey) {
      keysToClear.push(currentNode.answerKey);
    }
    if (previousFlowNode.answerKey) {
      keysToClear.push(previousFlowNode.answerKey);
    }
    if (previousFlowNode.extraAnswerKeys) {
      keysToClear.push(...previousFlowNode.extraAnswerKeys);
    }

    if (!keysToClear.length) {
      return nextState;
    }

    return clearFutureAnswers(nextState, [...new Set(keysToClear)]);
  }

  const currentNode = getFlowNode(state.currentNodeId);
  if (currentNode.kind === 'result' || !currentNode.answerKey) {
    return state;
  }

  const nextAnswers = { ...state.answers, [currentNode.answerKey]: action.option.value, ...action.option.extraAnswers };
  const nextAnswerLabels = { ...state.answerLabels, [currentNode.answerKey]: action.option.label };

  if (action.type === 'answer') {
    return { ...state, answers: nextAnswers, answerLabels: nextAnswerLabels };
  }

  return {
    ...state,
    currentNodeId: action.nextNodeId,
    history: [...state.history, state.currentNodeId],
    answers: nextAnswers,
    answerLabels: nextAnswerLabels,
  };
};

const AppStateProvider = ({ children }: PropsWithChildren) => {
  const [showFitAssistant, setShowFitAssistant] = useState(false);
  const [flowState, dispatch] = useReducer(reducer, initialFlowState);
  // _THULEDATA.FitAssistantBikes is a shared, polymorphic key: the backend fills it with whichever
  // fit-assistant payload matches the page. It's typed globally as the BikeAdapters shape, so cast to
  // the RMCBS shape that this app is actually served.
  const [rearMountedChildBikeSeatsData] = useState<RearMountedChildBikeSeatsData | null>(
    (window._THULEDATA?.FitAssistantBikes as unknown as RearMountedChildBikeSeatsData) ?? null
  );
  const [fitQueryResult, setFitQueryResult] = useState<AppState['fitQueryResult']>(undefined);

  useEffect(() => {
    const savedAnswers = loadCbsAnswers();
    if (!savedAnswers || Object.keys(savedAnswers).length === 0) return;

    (async () => {
      let currentNodeId: NodeId = FLOW_START_NODE;
      const history: NodeId[] = [];
      let ctx: FlowContext = { answers: savedAnswers };
      let restoredFitQueryResult: RearMountedChildBikeSeatsResponse | undefined;

      while (true) {
        const node = getFlowNode(currentNodeId);
        if (node.kind === 'result' || !node.answerKey || !node.getNext) break;

        const answer = savedAnswers[node.answerKey];
        if (answer === undefined || answer === null) break;

        if (node.fetchContextData) {
          try {
            const ctxWithAnswer: FlowContext = { ...ctx, answers: { ...ctx.answers, [node.answerKey]: answer } };
            const extra = await node.fetchContextData(ctxWithAnswer);
            ctx = { ...ctxWithAnswer, ...extra };
            if (extra.fitQueryResult) {
              restoredFitQueryResult = extra.fitQueryResult as RearMountedChildBikeSeatsResponse;
            }
            if (node.onFetchComplete) {
              node.onFetchComplete(ctx);
            }
          } catch {
            break;
          }
        }

        const nextNodeId = node.getNext(ctx, answer);
        if (nextNodeId === 'fit') break;
        history.push(currentNodeId);
        currentNodeId = nextNodeId;
      }

      dispatch({ type: 'restore', flowState: { currentNodeId, history, answers: savedAnswers, answerLabels: {} } });
      if (restoredFitQueryResult) {
        setFitQueryResult(restoredFitQueryResult);
      }
    })();
  }, []);

  useEffect(() => {
    if (!flowState.answers.frameType) return;
    saveCbsAnswers(flowState.answers);
  }, [flowState.answers.frameType]);

  const selection = useMemo(
    () =>
      Object.entries(flowState.answerLabels)
        .filter(([key]) => flowState.answers[key as NodeId] !== 'yes' && flowState.answers[key as NodeId] !== 'no')
        .map(([, label]) => label)
        .filter(Boolean)
        .join(' / '),
    [flowState.answerLabels, flowState.answers]
  );

  const selectOption = useCallback(
    async (option: FlowOption) => {
      const currentNode = getFlowNode(flowState.currentNodeId);

      if (currentNode.kind === 'result' || !currentNode.answerKey || !currentNode.getNext) {
        return;
      }

      const nextAnswers = { ...flowState.answers, [currentNode.answerKey]: option.value };
      let ctx: FlowContext = { answers: nextAnswers };

      if (currentNode.fetchContextData) {
        const extra = await currentNode.fetchContextData(ctx);
        ctx = { ...ctx, ...extra };
        if (extra.fitQueryResult) {
          setFitQueryResult(extra.fitQueryResult);
        }
      }

      if (currentNode.onFetchComplete) {
        currentNode.onFetchComplete(ctx);
      }

      const nextNodeId = option.nextNodeId || currentNode.getNext(ctx, option.value);

      if (nextNodeId === 'fit') {
        dispatch({ type: 'answer', option });
        setShowFitAssistant(false);
        return;
      }

      dispatch({ type: 'select', option, nextNodeId });
    },
    [flowState.currentNodeId, flowState.answers]
  );

  const goBack = useCallback(() => {
    const currentNode = getFlowNode(flowState.currentNodeId);
    if ((currentNode.kind === 'result' || fitQueryResult) && !IsProductPage) {
      ResetFitStatusForAllProductsInListing();
    }
    dispatch({ type: 'back' });
  }, [flowState.currentNodeId, fitQueryResult]);

  const resetFlow = useCallback(() => {
    dispatch({ type: 'reset' });
    setFitQueryResult(undefined);
    removeCbsAnswers();
    ResetFitStatusForAllProductsInListing();
  }, []);

  const isConfirmedFitStep = useMemo(() => {
    if (!fitQueryResult) return false;
    const currentNode = getFlowNode(flowState.currentNodeId);
    return !!currentNode.answerKey && !!flowState.answers[currentNode.answerKey];
  }, [fitQueryResult, flowState.currentNodeId, flowState.answers]);

  const value: AppState = {
    showFitAssistant,
    setShowFitAssistant,
    currentNodeId: flowState.currentNodeId,
    selectOption,
    goBack,
    resetFlow,
    canGoBack: flowState.history.length > 0,
    rearMountedChildBikeSeatsData,
    answers: flowState.answers,
    selection,
    fitQueryResult,
    isConfirmedFitStep,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};

const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

export { AppStateProvider, useAppState };
